/* High Risk Alerts — full-screen page behind the red dashboard card.
 *
 * Replaces the previous "one card per bus, including every bus with nothing
 * wrong" list. The unit is now the ALERT, sorted worst-first, with the selected
 * one explained in full on the right.
 *
 * ⚠️ ZANA TAKEOVER CONTRACT — do not remove these hooks:
 *   data-zt="alertList"  the scroll container Zana spotlights for `tab: highRisk`
 *   data-zt="markAll"    the mark-all-read control
 *   data-zt="alert0..7"  the first eight UNREAD alerts, in unread order
 * `zanaTakeover.ts` resolves these by name (see its `step.tab === 'highRisk'`
 * branch). They fail closed and silent — a missing hook means the narration
 * highlights nothing and looks like Zana is broken.
 *
 * HONESTY NOTES — things deliberately NOT drawn:
 *  · No pin for "where it happened". `fleetAlerts` carries no coordinates and no
 *    trip id, so any pin would be invented. The map shows the bus's last
 *    completed route and says so.
 *  · Severity is derived from the alert type (see `alertMeta`), because the
 *    documents have no severity field. Only incidents carry a real one.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  FaArrowLeft, FaExclamationTriangle, FaGasPump, FaRoute, FaSatelliteDish,
  FaCarSide, FaInfoCircle, FaSearch, FaCheck,
} from 'react-icons/fa';
import type { FleetAlertRow } from '../../fleet/useFleetAlerts';
import { useLockBodyScroll } from '../../fleet/useLockBodyScroll';
import CorrectRefuelPanel from './CorrectRefuelPanel';
import BusMiniMap from './BusMiniMap';
import { alertInvitesRefuelCorrection } from '../../services/refuelCorrection';
import {
  severityOf, labelOf, kindOf, groupOf, severityRank, relativeTime, dayLabel, monogram,
  reviewReasonSentence,
  type FleetSeverity, type FleetAlertGroup,
} from '../../fleet/alertMeta';

/** Alerts written before 2026-08-22 glued "Bus " onto a nickname that already
    began with it ("Bus Bus KCE999Z"). The writers are fixed; the stored rows
    are not, so the display tidies what history left behind. (Lives BELOW the
    imports — CRA's eslint `import/first` failed the Render build when it sat
    between them.) */
export function tidyBusName(s: string | null | undefined): string {
  return String(s ?? '').replace(/\bBus Bus\b/g, 'Bus');
}

/**
 * Put the reason back into a flagged-refuel alert.
 *
 * The app words 4 of the 8 reasons it can flag a fill for; the other 4 — all
 * three odometer verdicts and `spans_unmetered_fill` — arrive as the bare
 * sentence "<bus>: this refuel needs review.", which tells the owner nothing
 * and, headed "Fuel needs review", names the wrong subsystem for an odometer
 * fault. The reason IS on the document; it was simply never read.
 *
 * Applied on the way into the row, so the list and the detail hero agree, and
 * so every alert ALREADY WRITTEN is repaired without an app build.
 */
export function messageWithReason(message: string, reviewReason?: string): string {
  const reason = reviewReasonSentence(message, reviewReason);
  if (!reason) return message;
  const stem = message.replace(/this refuel needs review\.?\s*$/i, '').trim();
  const sentence = `${reason.charAt(0).toUpperCase()}${reason.slice(1)}.`;
  return stem ? `${stem} ${sentence}` : sentence;
}

export interface HighRiskBus {
  id: string;
  name: string;
  plate: string;
  status: string;
  drivers: string[];
  childCount: number;
  liveLocationUpdatedAt?: number | null;
  /** Litres. Absent on every bus created before `59681fe` made it compulsory. */
  fuelTankCapacityL?: number | null;
  /** The bus's first odometer reading, from the trip-start prompt or the first
   *  fill — whichever the driver reached first. Absent means no odometer has
   *  ever been recorded for this bus, so no fill can be measured. */
  odometerBaselineKm?: number | null;
  /** Does any refuel on file carry an odometer? `null`/undefined means the
   *  refuel history has not loaded yet — NOT that there is none. Buses that
   *  predate `odometerBaselineKm` carry their readings only in that history. */
  hasRefuelOdometer?: boolean | null;
}

export interface HighRiskIncident {
  busId: string;
  date: Date;
  severity: string;
  description: string;
}

/** A finished trip, used only for the route drawing and the trip ribbon. */
export interface HighRiskTrip {
  started: Date | null;
  finished: Date | null;
  km: number | null;
  childrenTotal: number;
  childrenAccounted: number;
  routePoints: { lat: number; lng: number; estimated?: boolean }[];
}

export interface HighRiskScreenProps {
  buses: HighRiskBus[];
  alerts: FleetAlertRow[];
  incidents: HighRiskIncident[];
  /** 30-day distance per bus, from the same summary the Fuel Report uses. */
  kmByBusId: Record<string, number>;
  /** 30-day fuel cost per bus (KES), from the cost estimate loader. */
  costByBusId: Record<string, number>;
  /** Most recent finished trip per bus, for the map and ribbon. */
  lastTripByBusId: Record<string, HighRiskTrip | undefined>;
  onClose: () => void;
  onOpenBus: (busId: string) => void;
  /** Row to open on arrival — `a:<alertId>` from a notification click, so the
      owner lands ON the alert rather than on a list with it somewhere inside. */
  initialSelectedKey?: string | null;
  /** Opens the admin tank editor. Used by the derived "no tank size" row. */
  onSetTank?: (busId: string) => void;
  onMarkRead: (alertId: string) => void | Promise<void>;
  onMarkAllRead: () => void | Promise<void>;
  onAskZana?: () => void;
}

/** One row in the left rail. Incidents are folded in as synthetic rows so the
 *  list answers "what needs me", not "what happens to live in fleetAlerts". */
type Row = {
  key: string;
  alertId: string | null;
  busId: string;
  title: string;
  message: string;
  at: Date;
  read: boolean;
  severity: FleetSeverity;
  group: FleetAlertGroup;
  type: string;
  isIncident: boolean;
  incidentSeverity?: string;
};

const KIND_ICON: Record<string, React.ReactElement> = {
  incident: <FaExclamationTriangle />,
  fuel: <FaGasPump />,
  route: <FaRoute />,
  gps: <FaSatelliteDish />,
  traffic: <FaCarSide />,
  info: <FaInfoCircle />,
};

function sevClass(prefix: string, sev: FleetSeverity): string {
  return sev === 'crit' ? `${prefix}-crit` : sev === 'warn' ? `${prefix}-warn` : `${prefix}-brand`;
}

/* The route card used to draw the trip as a bare SVG line on a lilac panel —
   a sketch with no roads. Gabriel, 2026-08-23: "the map doesn't work, just
   shows the purple line." It is now the same Google map the bus page uses
   (BusMiniMap, fitted to the trail). */

export default function HighRiskScreen({
  buses, alerts, incidents, kmByBusId, costByBusId, lastTripByBusId,
  onClose, onOpenBus, initialSelectedKey, onSetTank, onMarkRead, onMarkAllRead, onAskZana,
}: HighRiskScreenProps) {
  useLockBodyScroll();
  const [filter, setFilter] = useState<'open' | 'incident' | 'fuel' | 'route' | 'read'>('open');
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(initialSelectedKey ?? null);
  useEffect(() => {
    if (initialSelectedKey) setSelectedKey(initialSelectedKey);
  }, [initialSelectedKey]);

  /* Keyed case-insensitively, and by plate as well as id.
   *
   * Alert documents are written by several paths and they do not all normalise
   * the plate: Gabriel's incident arrived as `kce999z` while every other row
   * said `KCE999Z` (2026-08-04). An exact-match lookup missed it, so the rail
   * fell back to printing the raw id — a lowercase plate where a bus name
   * should be. The same casing trap cost us bug 21, where a lower-cased
   * plateKey wrote a preference key nothing could read back.
   *
   * Matching loosely here fixes every writer at once. Normalising at each
   * write site is still worth doing, but it cannot repair rows already stored. */
  const busById = useMemo(() => {
    const m: Record<string, HighRiskBus> = {};
    for (const b of buses) {
      m[b.id] = b;
      if (b.id) m[b.id.toUpperCase()] = b;
      if (b.plate) m[b.plate.toUpperCase()] = b;
    }
    return m;
  }, [buses]);
  const busFor = (id: string): HighRiskBus | undefined =>
    busById[id] || busById[(id || '').toUpperCase()];

  /* Every alert plus every incident, as one list. Incidents are not in
     `fleetAlerts` — they live on the bus document — but a page called High Risk
     that omitted them would be lying by omission, and the old page showed them
     too. */
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = alerts.map((a) => ({
      key: `a:${a.id}`,
      alertId: a.id,
      busId: a.busId,
      title: labelOf(a.type),
      message: tidyBusName(messageWithReason(a.message, a.reviewReason)),
      at: a.timestamp,
      read: a.read,
      severity: severityOf(a.type),
      group: groupOf(a.type),
      type: a.type,
      isIncident: false,
    }));
    incidents.forEach((c, i) => {
      out.push({
        key: `i:${c.busId}:${c.date.getTime()}:${i}`,
        alertId: null,
        busId: c.busId,
        title: `Incident — ${c.severity}`,
        message: c.description,
        at: c.date,
        // An incident has no read flag; it clears when it is resolved, not read.
        read: false,
        severity: 'crit',
        group: 'incident',
        type: 'incident',
        isIncident: true,
        incidentSeverity: c.severity,
      });
    });
    /* A bus with no tank size (bug 69, Gabriel's call 2026-08-02).
     *
     * Signup used to demand a capacity (`59681fe`); that is now the admin's
     * job on the bus. A driver JOINING an existing bus never saw it anyway.
     * Production currently has 22 buses and NOT ONE carries `fuelCapacityLiters`.
     *
     * The consequence is quiet and total: the Fuel Engine falls back to a
     * seat-count guess (100 L on a 30-seater), and every litre figure,
     * cost-per-litre, range estimate and theft threshold is arithmetic against
     * a number nobody entered. Those numbers do not look wrong — they look
     * precise. That is what makes this worth the top of the queue rather than a
     * line in a settings page.
     *
     * DERIVED, not written to `fleetAlerts`: it is a configuration gap, not an
     * event. An alert doc would need clearing when someone fixes the bus, and a
     * stale "no tank size" on a bus that now has one is exactly the kind of
     * noise that teaches an office to ignore the list. This row disappears the
     * moment a capacity is saved. */
    buses.forEach((b) => {
      const cap = b.fuelTankCapacityL;
      if (typeof cap === 'number' && cap > 0) return;
      out.push({
        key: `cap:${b.id}`,
        alertId: null,
        busId: b.id,
        title: 'No tank size on this bus',
        message:
          'Fuel figures for this bus are guessed from seat count until an admin sets the real tank size. '
          + 'Litres, cost and any fuel-loss warning will be wrong until then. '
          + 'Set it on this bus — the row disappears the moment a capacity is saved. You cannot clear it by marking it read.',
        /* `new Date(0)` filed this under "JANUARY 1, 1970" in the day-grouped
         * rail (Gabriel, 2026-08-03). The row has no event time — it is a
         * standing fact about the bus, not something that happened — and the
         * list groups by day, so the absence of a date has to be represented
         * rather than faked. `now` keeps it beside today's work, which is where
         * a configuration gap you can fix right now belongs. */
        at: new Date(),
        read: false,
        severity: 'warn',
        group: 'fuel',
        type: 'missing_tank_capacity',
        isIncident: false,
      });
    });
    /* A bus with no odometer reading on file — the same shape of gap as the
     * tank size above, and derived for the same reasons.
     *
     * Tank size makes litres wrong; a missing odometer makes fuel economy not
     * exist at all. A fill's km/L is the distance BETWEEN two readings, so the
     * first reading only opens the bracket — until one is recorded, every fill
     * this bus logs is a cost with no efficiency, and the Fuel Report has
     * nothing to rate the driver against.
     *
     * Unlike tank size there is no field here for an admin to type into, and
     * that is deliberate: the number lives on the dashboard of the bus, and
     * the only person who can read it is the driver. So the row explains that
     * the phone will ask at the next trip start, and clears itself when it is
     * answered — by that prompt or by the first refuel, whichever arrives
     * first. Marking it read does nothing. */
    buses.forEach((b) => {
      const odo = b.odometerBaselineKm;
      if (typeof odo === 'number' && odo > 0) return;
      /* Only accuse a bus when we POSITIVELY know it has nothing. `false` is
       * the loaded answer "no fill on this bus has ever carried an odometer";
       * null/undefined is "the refuel history has not come back yet", and a
       * bus with six readings must not be listed as having none while a query
       * is in flight. */
      if (b.hasRefuelOdometer !== false) return;
      out.push({
        key: `odo:${b.id}`,
        alertId: null,
        busId: b.id,
        title: 'No odometer reading yet',
        message:
          'Fuel economy for this bus cannot be calculated until someone records its odometer — km per litre is the distance between two readings, '
          + 'and this bus has none. The driver is asked for it each time they start a trip, and the first refuel records it too. '
          + 'This row clears itself the moment either happens. You cannot clear it by marking it read.',
        at: new Date(),
        read: false,
        severity: 'warn',
        group: 'fuel',
        type: 'missing_odometer_baseline',
        isIncident: false,
      });
    });
    return out.sort((x, y) => severityRank(x.severity) - severityRank(y.severity) || y.at.getTime() - x.at.getTime());
  }, [alerts, incidents, buses]);

  const counts = useMemo(() => {
    const unread = rows.filter((r) => !r.read && !r.isIncident).length;
    /* An INCIDENT is anything in the incident group — the filed reports AND the
     * alerts that are about one. Not just `isIncident`.
     *
     * That distinction was invisible in the code and absurd on the screen: a row
     * titled "Incident reported" sat in the list with a chip beside it reading
     * "Incidents · 0", and a tile saying "No incident reports outstanding".
     * Gabriel asked why twice (2026-08-04) and he was right both times.
     *
     * `isIncident` marks rows synthesised from `busIssueReports`. An alert of
     * type `incident` — which is what the driver's app writes alongside the
     * report — is NOT that, so it was excluded from a filter named after it.
     * Meanwhile the Fuel and Route chips two lines below have always filtered on
     * `group`. Incidents was the odd one out, and the reader has no way to know
     * the product draws a line between "an incident report" and "an alert about
     * an incident" — nor should they have to.
     *
     * `filed` keeps the narrower count for copy that genuinely means a REPORT,
     * since those clear by being resolved rather than by being read. */
    const isIncidentRow = (r: Row) => r.isIncident || r.group === 'incident';
    const inc = rows.filter(isIncidentRow).length;
    const filed = rows.filter((r) => r.isIncident).length;
    const open = rows.filter((r) => !r.read).length;
    const crit = rows.filter((r) => !r.read && r.severity === 'crit').length;
    const warn = rows.filter((r) => !r.read && r.severity === 'warn').length;
    const info = rows.filter((r) => !r.read && r.severity === 'info').length;
    /* Counted through `busFor`, not on the raw id, and CLAMPED to the fleet
     * size. Two things went wrong here on 2026-08-04 and the donut printed
     * "-1 / 2 buses clear".
     *
     * First, `kce999z` and `KCE999Z` are one bus but two strings, so a
     * two-bus fleet reported three affected. Resolving through `busFor` folds
     * the casings together the same way the rail and the hero already do.
     *
     * Second, a row can name a bus that is not in `buses` at all — a report
     * from a vehicle since removed, or one whose plate matches nothing. That
     * row still deserves to be visible, but it cannot make the fleet smaller
     * than it is. `clear` is what the donut renders; a negative there is a
     * number no reader can act on, and it discredits the three tiles beside
     * it that happen to be right. */
    const affectedKeys = new Set(
      rows.filter((r) => !r.read).map((r) => busFor(r.busId)?.id ?? (r.busId || '').toUpperCase()),
    );
    const busesAffected = Math.min(affectedKeys.size, buses.length);
    return { unread, inc, filed, open, crit, warn, info, busesAffected, fuel: rows.filter((r) => r.group === 'fuel' && !r.read).length, route: rows.filter((r) => r.group === 'route' && !r.read).length };
  }, [rows, busById, buses.length]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === 'open' && r.read) return false;
      if (filter === 'read' && !r.read) return false;
      // Group, not `isIncident` — see the note on `counts.inc`.
      if (filter === 'incident' && !(r.isIncident || r.group === 'incident')) return false;
      if (filter === 'fuel' && r.group !== 'fuel') return false;
      if (filter === 'route' && r.group !== 'route') return false;
      if (!q) return true;
      const b = busFor(r.busId);
      return (
        r.title.toLowerCase().includes(q) ||
        r.message.toLowerCase().includes(q) ||
        (b?.name ?? '').toLowerCase().includes(q) ||
        (b?.plate ?? '').toLowerCase().includes(q) ||
        (b?.drivers ?? []).join(' ').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, search, busById]);

  /* Zana addresses the first eight UNREAD alerts as alert0..alert7. That order
     must match the order the takeover script counted, which is unread order
     across the whole list — not the filtered view. */
  const unreadOrder = useMemo(() => {
    const ids: string[] = [];
    for (const r of rows) if (!r.read && r.alertId) ids.push(r.alertId);
    return ids;
  }, [rows]);

  const selected = useMemo(
    /* AN ALERT SOMEBODY ASKED FOR BY NAME OUTRANKS THE FILTER.
       ────────────────────────────────────────────────────────────────────
       This looked only in `visible`, and `visible` drops read alerts while the
       default 'open' filter is on. Tapping a notification MARKS IT READ and
       then opens this screen asking for it by key — so the click hid the alert
       from the only view about to show it, the find missed, and the fallback
       selected `visible[0]`: a different alert entirely. Gabriel, 2026-09-02:
       "when I press the notification, it does take me to alerts but not to
       that specific alert."

       Falling back through the FULL row set fixes it for every filter, not
       just this one — deep-linking a fuel alert while the rail is on 'route'
       used to fail the same way. `visible[0]` stays the last resort, for
       opening the screen with no particular alert in mind. */
    () => visible.find((r) => r.key === selectedKey)
      ?? rows.find((r) => r.key === selectedKey)
      ?? visible[0] ?? null,
    [visible, rows, selectedKey],
  );

  const bus = selected ? busFor(selected.busId) : undefined;

  /* Every per-bus lookup below keys on the RESOLVED bus id, never on the raw
   * `selected.busId`. The raw string is whatever the alert writer stored —
   * `kce999z` on the 2026-08-04 incident — while `lastTripByBusId`,
   * `kmByBusId` and `costByBusId` are keyed on bus DOCUMENT ids, and other
   * rows for the same bus may carry any casing. Raw matching is what left the
   * bus card claiming "Incidents on file: 0" and "Alerts on this bus: 1" while
   * the rail beside it listed five KCE999Z rows, one of them the filed report
   * itself — the hero and the rail had already learned this lesson (792f1e3);
   * these tiles were the last raw lookups in the file. */
  const busKey = bus?.id ?? (selected?.busId || '').toUpperCase();
  const rowBusKey = (r: Row) => busFor(r.busId)?.id ?? (r.busId || '').toUpperCase();
  const trip = selected ? lastTripByBusId[busKey] ?? lastTripByBusId[selected.busId] : undefined;

  const busAlerts = useMemo(
    () => (selected ? rows.filter((r) => rowBusKey(r) === busKey) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rowBusKey/busKey derive from rows+busById+selected
    [rows, selected, busById],
  );

  const byType = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of busAlerts) m.set(r.title, (m.get(r.title) ?? 0) + 1);
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [busAlerts]);

  const km = selected ? kmByBusId[busKey] ?? kmByBusId[selected.busId] ?? 0 : 0;
  const cost = selected ? costByBusId[busKey] ?? costByBusId[selected.busId] ?? 0 : 0;
  const costPerKm = km > 0 && cost > 0 ? cost / km : null;

  const grouped = useMemo(() => {
    const out: { label: string; items: Row[] }[] = [];
    for (const r of visible) {
      const label = dayLabel(r.at);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(r);
      else out.push({ label, items: [r] });
    }
    return out;
  }, [visible]);

  const hasRoute = !!trip && (trip.routePoints?.length ?? 0) >= 2;

  return (
    <div className="fd-screen">
      {/* ---------------- app bar ---------------- */}
      <header className="fd-appbar">
        <button type="button" className="fd-iconbtn" onClick={onClose} aria-label="Back">
          <FaArrowLeft />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1>High Risk Alerts</h1>
          <p className="fd-sub">
            {buses.length} bus{buses.length === 1 ? '' : 'es'}
            {counts.open > 0
              ? ` · ${counts.open} open across ${counts.busesAffected} bus${counts.busesAffected === 1 ? '' : 'es'}`
              : ' · nothing outstanding'}
          </p>
        </div>
        {onAskZana && (
          <button type="button" className="fd-btn fd-btn-primary fd-btn-sm" onClick={onAskZana}>
            Ask Zana about these
          </button>
        )}
        {counts.unread > 0 && (
          <button type="button" data-zt="markAll" className="fd-btn fd-btn-quiet fd-btn-sm" onClick={() => void onMarkAllRead()}>
            Mark all read
          </button>
        )}
      </header>

      {/* ---------------- overview strip ---------------- */}
      <section className="fd-strip" style={{ gridTemplateColumns: '1.15fr 1fr 1fr' }}>
        <div className="fd-cell">
          <span className="fd-kicker">Open right now</span>
          <div className="fd-big fd-num">
            {counts.open}
            <small>across {counts.busesAffected} bus{counts.busesAffected === 1 ? '' : 'es'}</small>
          </div>
          {counts.open > 0 && (
            <>
              <div className="fd-sevbar">
                <i style={{ width: `${(counts.crit / counts.open) * 100}%`, background: 'var(--fd-crit-600)' }} />
                <i style={{ width: `${(counts.warn / counts.open) * 100}%`, background: 'var(--fd-warn-600)' }} />
                <i style={{ width: `${(counts.info / counts.open) * 100}%`, background: 'var(--fd-brand-400)' }} />
              </div>
              <div className="fd-sevlegend">
                <span><span className="fd-dot fd-dot-crit" />{counts.crit} serious</span>
                <span><span className="fd-dot fd-dot-warn" />{counts.warn} warning{counts.warn === 1 ? '' : 's'}</span>
                <span><span className="fd-dot fd-dot-brand" />{counts.info} info</span>
              </div>
            </>
          )}
        </div>

        <div className="fd-cell">
          <span className="fd-kicker">Incidents</span>
          <div className="fd-big fd-num">{counts.inc}</div>
          {/* The copy has to survive all three cases, because the tile used to
              print "No incident reports outstanding" directly beside a serious
              incident alert — a flat contradiction, and the reason this whole
              thing was mistaken for a data bug for an afternoon. */}
          <p className="fd-cellsub">
            {counts.inc === 0
              ? 'Nothing reported'
              : counts.filed === 0
                ? `${counts.inc === 1 ? 'Raised by an alert' : 'Raised by alerts'} — clears when marked read`
                : `${counts.filed} filed as ${counts.filed === 1 ? 'a report' : 'reports'} — those clear when resolved, not by marking alerts read`}
          </p>
        </div>

        <div className="fd-cell">
          <span className="fd-kicker">Fleet status</span>
          <div className="fd-donutwrap">
            <FleetDonut clear={buses.length - counts.busesAffected} total={buses.length} />
            <div className="fd-donutlabel">
              <b className="fd-num">{buses.length - counts.busesAffected} / {buses.length}</b>
              <span>buses clear</span>
              {counts.busesAffected > 0 && (
                <p className="fd-cellsub">{counts.busesAffected} need attention</p>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="fd-split">
        {/* ---------------- left rail ---------------- */}
        <aside className="fd-rail">
          <div className="fd-railtop">
            <div className="fd-search">
              <FaSearch style={{ color: 'var(--fd-ink-400)', flex: '0 0 auto' }} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search bus, plate or driver"
                aria-label="Search alerts"
              />
            </div>
            <div className="fd-chips">
              {([
                ['open', `Open · ${counts.open}`],
                ['incident', `Incidents · ${counts.inc}`],
                ['fuel', `Fuel · ${counts.fuel}`],
                ['route', `Route · ${counts.route}`],
                ['read', 'Read'],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className="fd-chip"
                  aria-pressed={filter === key}
                  onClick={() => { setFilter(key); setSelectedKey(null); }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div data-zt="alertList" className="fd-raillist">
            {visible.length === 0 && (
              <div className="fd-empty">
                <FaCheck size={26} />
                <b>Nothing here</b>
                <span>{filter === 'open' ? 'No open alerts across the fleet.' : 'Nothing matches this filter.'}</span>
              </div>
            )}
            {grouped.map((g) => (
              <div key={g.label}>
                <p className="fd-groupname">{g.label}</p>
                {g.items.map((r) => {
                  const b = busFor(r.busId);
                  const unreadIdx = r.alertId ? unreadOrder.indexOf(r.alertId) : -1;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      data-zt={unreadIdx >= 0 && unreadIdx < 8 ? `alert${unreadIdx}` : undefined}
                      className={`fd-item${r.read ? ' fd-item-read' : ''}`}
                      aria-current={selected?.key === r.key ? "true" : undefined}
                      onClick={() => setSelectedKey(r.key)}
                    >
                      <span className={`fd-ig ${sevClass('fd-ig', r.severity).replace('fd-ig-brand', 'fd-ig-brand')}`}>
                        {KIND_ICON[kindOf(r.type)] ?? KIND_ICON.info}
                      </span>
                      <span className="fd-itxt">
                        <span className="fd-ititle" style={{ display: 'block' }}>{r.title}</span>
                        <span className="fd-imeta">
                          <span>{b?.plate || r.busId}</span>
                          <span className="fd-sep">·</span>
                          <span>{relativeTime(r.at)}</span>
                        </span>
                      </span>
                      {!r.read && <span className="fd-unread" />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </aside>

        {/* ---------------- detail ---------------- */}
        <section className="fd-detail">
          {!selected && (
            <div className="fd-empty" style={{ paddingTop: 120 }}>
              <FaCheck size={30} />
              <b>Nothing needs you right now</b>
              <span>Every bus in the fleet is clear of open alerts and incidents.</span>
            </div>
          )}

          {selected && (
            <>
              <div className="fd-hero">
                <div className="fd-heroinner">
                  <div style={{ minWidth: 0 }}>
                    <span className={`fd-pill fd-pill-onbrand`}>
                      {selected.isIncident
                        ? `Incident · ${selected.incidentSeverity ?? 'reported'} · open`
                        : `${selected.severity === 'crit' ? 'Serious' : selected.severity === 'warn' ? 'Warning' : 'Information'} · ${selected.read ? 'read' : 'unread'}`}
                    </span>
                    <h2>{selected.message || selected.title}</h2>
                    <div className="fd-herometa">
                      <span className="fd-avatar">{monogram(bus?.name ?? '')}</span>
                      <span style={{ color: '#fff', fontWeight: 600 }}>{bus?.name ?? 'Unknown bus'}</span>
                      {bus?.plate && <span className="fd-plate">{bus.plate}</span>}
                      {bus?.drivers?.[0] && (<><span>·</span><span>{bus.drivers[0]}</span></>)}
                      <span>·</span>
                      <span>{selected.at.toLocaleString()} · {relativeTime(selected.at)}</span>
                    </div>
                  </div>
                  <div className="fd-heroactions">
                    {!selected.read && selected.alertId && (
                      <button type="button" className="fd-btn fd-btn-onbrand fd-btn-sm" onClick={() => void onMarkRead(selected.alertId as string)}>
                        Mark read
                      </button>
                    )}
                    <button type="button" className="fd-btn fd-btn-white fd-btn-sm" onClick={() => (
                      selected.type === 'missing_tank_capacity' && onSetTank
                        ? onSetTank(selected.busId)
                        : onOpenBus(selected.busId)
                    )}>
                      {selected.type === 'missing_tank_capacity' ? 'Set tank size' : 'Open bus'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="fd-tiles">
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Alerts on this bus</span>
                  <p className="fd-tileval fd-num">{busAlerts.length}</p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>
                    {busAlerts.filter((r) => !r.read).length} still open
                  </p>
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Distance · 30 d</span>
                  <p className="fd-tileval fd-num">{km > 0 ? `${Math.round(km)} km` : '—'}</p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>From finished routes</p>
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Fuel cost · 30 d</span>
                  <p className="fd-tileval fd-num">{cost > 0 ? cost.toLocaleString() : '—'}</p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>KES, estimated</p>
                </div>
                <div className="fd-card fd-tile">
                  <span className="fd-kicker">Cost per km</span>
                  <p className="fd-tileval fd-num">{costPerKm ? costPerKm.toFixed(1) : '—'}</p>
                  <p className="fd-tiny fd-muted" style={{ margin: '6px 0 0' }}>
                    {costPerKm ? 'KES/km' : 'Needs distance and a logged refuel'}
                  </p>
                </div>
              </div>

              {/* Correcting a logged refuel — ONLY under an alert that says the
                  fuel figures contradict something. Not a general edit button:
                  see the note in `refuelCorrection`. */}
              {!selected.isIncident && alertInvitesRefuelCorrection(selected.type) && bus && (
                <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                  <h3>Were the figures wrong?</h3>
                  <CorrectRefuelPanel
                    busId={selected.busId}
                    busLabel={bus.plate || bus.name || 'this bus'}
                  />
                </div>
              )}

              <div className="fd-grid">
                <div>
                  {/* route map */}
                  <div className="fd-card fd-mapcard">
                    <div className="fd-maphead">
                      <h3 style={{ margin: 0 }}>This bus&rsquo;s last completed route</h3>
                      {trip?.km ? <span className="fd-pill fd-pill-info" style={{ marginLeft: 'auto' }}>{trip.km.toFixed(1)} km</span> : null}
                    </div>
                    <div className="fd-mapbox">
                      {hasRoute && trip ? (
                        <BusMiniMap
                          routePoints={trip.routePoints}
                          fitToRoute
                          height={300}
                          emptyMessage="This bus has no finished trip with GPS points stored."
                        />
                      ) : (
                        <div className="fd-empty" style={{ padding: '40px 24px' }}>
                          <b>No route recorded</b>
                          <span>This bus has no finished trip with GPS points stored.</span>
                        </div>
                      )}
                    </div>
                    <div className="fd-maplegend">
                      <span><i className="fd-swatch" style={{ background: '#2563eb' }} /> Route driven</span>
                      <span><span className="fd-nodata">No pin</span> Alerts do not record a location, so nothing is marked on the path.</span>
                    </div>
                  </div>

                  {/* trip ribbon */}
                  {trip?.started && trip?.finished && (
                    <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                      <h3>That trip, start to finish</h3>
                      <TripRibbon trip={trip} markerAt={selected.at} />
                      {/* Sorted by clock, not by role.
                          These three rows used to render started → alert →
                          completed in fixed order, so an alert raised AFTER the
                          route ended was drawn between them: Gabriel's trip read
                          "04:22 started · 05:21 fuel loss · 05:10 completed"
                          (2026-08-03). A timeline that is not in time order is
                          worse than no timeline — it invents a sequence of
                          events, and here it implied the fuel warning happened
                          during the run when it landed eleven minutes after it. */}
                      {(() => {
                        const outside =
                          selected.at.getTime() < trip.started!.getTime()
                          || selected.at.getTime() > trip.finished!.getTime();
                        const hhmm = (d: Date) =>
                          d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        const rows = [
                          {
                            key: 'start',
                            at: trip.started!,
                            cls: 'fd-tlrow',
                            title: `${hhmm(trip.started!)} — route started`,
                            body: `${trip.childrenTotal} child${trip.childrenTotal === 1 ? '' : 'ren'} expected.`,
                          },
                          {
                            key: 'alert',
                            at: selected.at,
                            cls: `fd-tlrow ${sevClass('fd-tlrow', selected.severity)}`,
                            title: `${hhmm(selected.at)} — ${selected.title.toLowerCase()}`
                              + (outside ? ' · after the route ended' : ''),
                            body: selected.message,
                          },
                          {
                            key: 'end',
                            at: trip.finished!,
                            cls: 'fd-tlrow',
                            title: `${hhmm(trip.finished!)} — route completed`,
                            body: `${trip.childrenAccounted} of ${trip.childrenTotal} accounted for`
                              + (trip.km ? ` · ${trip.km.toFixed(1)} km` : '') + '.',
                          },
                        ].sort((a, b) => a.at.getTime() - b.at.getTime());
                        return (
                          <div className="fd-tl" style={{ marginTop: 8 }}>
                            {rows.map((r) => (
                              <div key={r.key} className={r.cls}>
                                <b>{r.title}</b>
                                <span>{r.body}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* alerts by type */}
                  {byType.length > 0 && (
                    <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                      <h3>What this bus gets flagged for<span className="fd-rest">All time on file</span></h3>
                      <div className="fd-bars">
                        {byType.map(([label, n]) => {
                          const max = byType[0][1] || 1;
                          return (
                            <div className="fd-bar" key={label}>
                              <span className="fd-lbl">{label}</span>
                              <span className="fd-trk"><i className="fd-fil" style={{ width: `${(n / max) * 100}%` }} /></span>
                              <span className="fd-val fd-num">{n}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* right column */}
                <div>
                  <div className="fd-card fd-buscard">
                    <div className="fd-bctop">
                      <span className="fd-avatar" style={{ borderColor: 'rgba(255,255,255,.5)' }}>{monogram(bus?.name ?? '')}</span>
                      <div>
                        <b>{bus?.name ?? 'Unknown bus'}</b>
                        <span>{bus?.plate ?? ''}{bus?.drivers?.[0] ? ` · ${bus.drivers[0]}` : ''}</span>
                      </div>
                    </div>
                    <div className="fd-bcinner">
                      <div className="fd-bcrow">
                        <span>Status</span>
                        <span>
                          <span className={`fd-pill ${bus?.status === 'active' ? 'fd-pill-ok' : 'fd-pill-warn'}`}>
                            <span className={`fd-dot ${bus?.status === 'active' ? 'fd-dot-ok' : 'fd-dot-warn'}`} />
                            {bus?.status === 'active' ? 'In service'
                              : bus?.status === 'for_hire' ? 'For hire'
                              : bus?.status === 'suspended' ? 'Suspended' : 'Maintenance'}
                          </span>
                        </span>
                      </div>
                      <div className="fd-bcrow">
                        <span>Last seen</span>
                        <span>{bus?.liveLocationUpdatedAt ? relativeTime(new Date(bus.liveLocationUpdatedAt)) : '—'}</span>
                      </div>
                      <div className="fd-bcrow"><span>Children on roster</span><span>{bus?.childCount ?? 0}</span></div>
                      <div className="fd-bcrow"><span>Open alerts</span><span>{busAlerts.filter((r) => !r.read).length}</span></div>
                      <div className="fd-bcrow"><span>Incidents on file</span><span>{busAlerts.filter((r) => r.isIncident).length}</span></div>
                    </div>
                  </div>

                  <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                    <h3>Do next</h3>
                    <button type="button" className="fd-btn fd-btn-primary fd-btn-sm fd-btn-block" onClick={() => (
                      selected.type === 'missing_tank_capacity' && onSetTank
                        ? onSetTank(selected.busId)
                        : onOpenBus(selected.busId)
                    )}>
                      {selected.type === 'missing_tank_capacity' ? 'Set tank size' : 'Open bus page'}
                    </button>
                    {!selected.read && selected.alertId && (
                      <button type="button" className="fd-btn fd-btn-quiet fd-btn-sm fd-btn-block" onClick={() => void onMarkRead(selected.alertId as string)}>
                        Mark this read
                      </button>
                    )}
                    {counts.unread > 0 && (
                      <button type="button" className="fd-btn fd-btn-quiet fd-btn-sm fd-btn-block" onClick={() => void onMarkAllRead()}>
                        Mark all {counts.unread} read
                      </button>
                    )}
                  </div>

                  {selected.isIncident && (
                    <div className="fd-card fd-block" style={{ marginTop: 16 }}>
                      <h3>Why this stays</h3>
                      <p className="fd-tiny fd-muted" style={{ margin: 0 }}>
                        An incident report is not an alert. Marking alerts read will not move this —
                        it clears when the incident itself is resolved on the bus page, and the
                        report stays on that bus&rsquo;s history afterwards.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

/** Green/red ring: how much of the fleet is clear. */
function FleetDonut({ clear, total }: { clear: number; total: number }) {
  const r = 30, c = 2 * Math.PI * r;
  const pct = total > 0 ? Math.max(0, Math.min(1, clear / total)) : 1;
  return (
    <svg width="76" height="76" viewBox="0 0 76 76" aria-hidden="true">
      <circle cx="38" cy="38" r={r} fill="none" stroke="#efe9ff" strokeWidth="12" />
      <circle
        cx="38" cy="38" r={r} fill="none" stroke="#16a34a" strokeWidth="12"
        strokeDasharray={`${(c * pct).toFixed(1)} ${c.toFixed(1)}`}
        strokeLinecap="butt" transform="rotate(-90 38 38)"
      />
      {pct < 1 && (
        <circle
          cx="38" cy="38" r={r} fill="none" stroke="#dc2626" strokeWidth="12"
          strokeDasharray={`${(c * (1 - pct)).toFixed(1)} ${c.toFixed(1)}`}
          strokeDashoffset={`${(-c * pct).toFixed(1)}`}
          strokeLinecap="butt" transform="rotate(-90 38 38)"
        />
      )}
    </svg>
  );
}

/** The trip as one bar, with the alert marked where it fell inside it. */
function TripRibbon({ trip, markerAt }: { trip: HighRiskTrip; markerAt: Date }) {
  if (!trip.started || !trip.finished) return null;
  const s = trip.started.getTime(), e = trip.finished.getTime();
  const span = Math.max(1, e - s);
  const raw = (markerAt.getTime() - s) / span;
  const inside = raw >= 0 && raw <= 1;
  const pct = Math.max(0, Math.min(1, raw)) * 100;
  const fmt = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  /* An alert raised within a few percent of Start or Done used to draw its
     own dot and time ON TOP of that end marker — two "08:31 PM"s mashed into
     one unreadable label and a red dot inside the Done dot (Gabriel,
     2026-08-23: "something red on that line, no idea what it is"). Fold it
     into the end marker instead; the rows underneath still list every event
     in clock order. */
  const mergeStart = inside && pct <= 8;
  const mergeEnd = inside && pct >= 92;
  return (
    <div className="fd-ribbon">
      <span className="fd-track" />
      <span className="fd-mk fd-mk-start" style={{ left: '0%' }}>
        <b>{fmt(trip.started)}</b><i /><em>{mergeStart ? 'Start · alert' : 'Start'}</em>
      </span>
      {inside && !mergeStart && !mergeEnd && (
        <span className="fd-mk fd-mk-crit" style={{ left: `${pct}%` }}>
          <b>{fmt(markerAt)}</b><i /><em>Alert</em>
        </span>
      )}
      <span className="fd-mk fd-mk-end" style={{ left: '100%' }}>
        <b>{fmt(trip.finished)}</b><i /><em>{mergeEnd ? 'Done · alert' : 'Done'}</em>
      </span>
    </div>
  );
}
